import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerDirectoryChoice, UpdateCustomerDirectoryDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { FormField, Input, Select } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useFooterSave } from '../../components/settings/footerSave';
import { MASKED, SetupLink, SetupSteps } from './integrationUi';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';

/**
 * Which directory this install's CUSTOMER signs in from — Microsoft 365, or their own Google
 * Workspace.
 *
 * Configured here rather than at provisioning time because the values usually cannot be collected
 * before the server exists: a customer's Google Workspace administrator produces the OAuth client
 * only once there is an install to point them at. So an install is stood up, and pointed at a
 * directory afterwards, from this page.
 *
 * The client secret is write-only, exactly like the Figma and Canva ones: stored sealed, never read
 * back, and shown as a mask the admin can type over.
 */

const CLIENT_ID_HINT =
  'From the customer’s own Google Cloud project. Ends .apps.googleusercontent.com. Not secret.';
const SECRET_HINT =
  'Kept sealed on our server (write-only — never shown again). Treat it like a password in transit.';
const DOMAINS_HINT =
  'Comma-separated, e.g. rescuemeds.com. Only accounts on these domains may sign in. Leaving it empty permits nobody.';

export function CustomerDirectoryCard() {
  // Tenant-Admin-only, and HIDDEN rather than disabled for everyone else: the endpoint behind it
  // 403s for a non-admin, so rendering the card for an ordinary member would show them an error
  // box about settings they cannot see and have no reason to know exist.
  const isTenantAdmin = useIsTenantAdmin();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['customer-directory'],
    queryFn: ({ signal }) => api.getCustomerDirectory(signal),
    enabled: isTenantAdmin,
  });
  const view = query.data;

  const [directory, setDirectory] = useState<CustomerDirectoryChoice | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [domains, setDomains] = useState<string | null>(null);
  // Write-only: `secretTouched` is what distinguishes "typing a new secret" from "leave it alone".
  const [secret, setSecret] = useState('');
  const [secretTouched, setSecretTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Null local state means "unedited, show the server's value" — so a save, or someone else's
  // change, is reflected without a stale local copy winning.
  const effDirectory = directory ?? view?.directory ?? 'microsoft';
  const effClientId = clientId ?? view?.googleClientId ?? '';
  const effDomains = domains ?? view?.googleAllowedDomains ?? '';
  const secretDisplay = secretTouched ? secret : view?.googleClientSecretSet ? MASKED : '';

  // A change is a value that differs from the saved one, as it would be stored (trimmed): typing in a
  // field and putting its text back is no change. The same tests decide what is sent and what makes the
  // card dirty, which on App Settings drives the footer, the Unsaved changes dialog and the browser's
  // prompt.
  const typedClientId = clientId?.trim();
  const typedDomains = domains?.trim();
  const directoryChanged = directory !== null && directory !== view?.directory;
  const clientIdChanged = typedClientId !== undefined && typedClientId !== (view?.googleClientId ?? '').trim();
  const domainsChanged = typedDomains !== undefined && typedDomains !== (view?.googleAllowedDomains ?? '').trim();
  // Only a typed secret: focusing the box clears the mask, but leaving it empty keeps the stored one.
  const secretChanged = secretTouched && secret.trim() !== '';

  const save = useMutation({
    mutationFn: async () => {
      const dto: UpdateCustomerDirectoryDto = {};
      if (directory !== null && directoryChanged) dto.directory = directory;
      if (clientIdChanged) dto.googleClientId = typedClientId || null;
      if (domainsChanged) dto.googleAllowedDomains = typedDomains || null;
      // Only send the secret when one was actually typed — otherwise saving the client id would
      // blank a stored secret.
      if (secretChanged) dto.googleClientSecret = secret.trim();
      return api.updateCustomerDirectory(dto);
    },
    onSuccess: (next) => {
      setError(null);
      setDirectory(null);
      setClientId(null);
      setDomains(null);
      setSecret('');
      setSecretTouched(false);
      queryClient.setQueryData(['customer-directory'], next);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save these settings.'),
  });

  useEffect(() => {
    if (save.isSuccess) setError(null);
  }, [save.isSuccess]);

  const dirty = directoryChanged || clientIdChanged || domainsChanged || secretChanged;

  const toast = useToast();
  /** Saves the change; resolves to whether it worked (a failure shows the server's sentence here). */
  const saveChanges = async (): Promise<boolean> => {
    setError(null);
    try {
      await save.mutateAsync();
      return true;
    } catch {
      return false;
    }
  };
  // On App Settings the page's sticky Save footer saves this card, and its own Save stands down.
  // Discarding puts every field back to the server's values: null drafts, and the secret's mask.
  const { inFooter } = useFooterSave({
    id: 'customer-directory',
    dirty,
    save: saveChanges,
    discard: () => {
      setError(null);
      setDirectory(null);
      setClientId(null);
      setDomains(null);
      setSecret('');
      setSecretTouched(false);
    },
  });
  const showSaved = save.isSuccess && !dirty;

  const isGoogle = effDirectory === 'google';

  // After every hook, so the hook order is stable whatever the role.
  if (!isTenantAdmin) return null;

  return (
    <Card>
      <CardHeader
        title="Customer sign-in"
        description="Which directory the people using this site sign in from. Alloterra administrators always sign in with Microsoft, whatever is chosen here."
      />
      <CardBody className="space-y-5">
        {query.isLoading ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : query.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">
            Could not load these settings.
          </p>
        ) : (
          <>
            <FormField label="Directory" htmlFor="customer-directory">
              <Select
                id="customer-directory"
                value={effDirectory}
                onChange={(e) => {
                  setError(null);
                  setDirectory(e.target.value as CustomerDirectoryChoice);
                }}
              >
                <option value="microsoft">Microsoft 365 / Entra</option>
                <option value="google">Google Workspace</option>
              </Select>
            </FormField>

            {isGoogle && (
              <>
                <SetupSteps
                  title="What the customer's Google administrator needs"
                  action={
                    <SetupLink href="https://console.cloud.google.com/auth/clients">
                      Google Cloud Console
                    </SetupLink>
                  }
                  steps={[
                    <>
                      In their OWN Google Cloud project, create an OAuth client of type{' '}
                      <strong>Web application</strong>.
                    </>,
                    <>
                      Add this <strong>Authorized redirect URI</strong>:{' '}
                      <code className="break-all font-mono text-xs">{view?.redirectUri}</code>
                    </>,
                    <>
                      Add this <strong>Authorized JavaScript origin</strong>:{' '}
                      <code className="break-all font-mono text-xs">{view?.javascriptOrigin}</code>
                    </>,
                    <>Send back the Client ID and Client secret, and their Workspace domain.</>,
                  ]}
                />

                <FormField label="Google Client ID" htmlFor="google-client-id" hint={CLIENT_ID_HINT}>
                  <Input
                    id="google-client-id"
                    autoComplete="off"
                    value={effClientId}
                    placeholder="1234567890-abc.apps.googleusercontent.com"
                    onChange={(e) => {
                      setError(null);
                      setClientId(e.target.value);
                    }}
                  />
                </FormField>

                <FormField label="Google Client secret" htmlFor="google-client-secret" hint={SECRET_HINT}>
                  <Input
                    id="google-client-secret"
                    type="password"
                    autoComplete="off"
                    value={secretDisplay}
                    placeholder="GOCSPX-…"
                    onFocus={() => {
                      if (!secretTouched) {
                        setSecretTouched(true);
                        setSecret('');
                      }
                    }}
                    onChange={(e) => {
                      setError(null);
                      setSecretTouched(true);
                      setSecret(e.target.value);
                    }}
                  />
                </FormField>

                <FormField
                  label="Allowed Workspace domains"
                  htmlFor="google-allowed-domains"
                  hint={DOMAINS_HINT}
                >
                  <Input
                    id="google-allowed-domains"
                    autoComplete="off"
                    value={effDomains}
                    placeholder="rescuemeds.com"
                    onChange={(e) => {
                      setError(null);
                      setDomains(e.target.value);
                    }}
                  />
                </FormField>

                {/* The readiness line is the point of this card: it says, in words, exactly what is
                    still missing — rather than leaving the admin to discover it as a failed login. */}
                <div className="space-y-1">
                  {view?.ready ? (
                    <span className="inline-flex items-center rounded-full bg-success-tint px-2 py-0.5 text-xs font-medium text-success dark:bg-success-dark-tint dark:text-success-dark">
                      Google sign-in configured
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
                      Not ready yet
                    </span>
                  )}
                  {view && !view.ready && view.missing.length > 0 && (
                    <ul className="list-inside list-disc text-sm text-ink-muted">
                      {view.missing.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  )}
                  {view?.ready && (
                    <p className="text-sm text-ink-muted">
                      Google sign-in is configured. Their staff can sign in once you have added them
                      as members below.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* With the footer saving, the row keeps only the "Saved." and error lines, and is left out
                when it has neither. */}
            {(!inFooter || showSaved || error) && (
              <div className="flex flex-wrap items-center gap-3">
                {!inFooter && (
                  <Button
                    loading={save.isPending}
                    disabled={!dirty || save.isPending}
                    onClick={() => {
                      if (!dirty) return;
                      void saveChanges().then((ok) => {
                        if (ok) toast.show(CHANGES_SAVED);
                      });
                    }}
                  >
                    Save
                  </Button>
                )}
                {showSaved && (
                  <span role="status" className="text-sm text-ink-muted">
                    Saved.
                  </span>
                )}
                {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
